import React, { createContext, useContext, useMemo } from 'react';
import {
  PolicyEditFieldKey,
  PolicyEditPermissions,
  getPolicyEditPermissions,
} from '../utils/policyEditPermissions';
import { Policy } from '../models/policy/Policy';

/**
 * PolicyEdit's form tree stitches together several nested components
 * (VehicleInformationForm, CustomerInformationForm, etc.) that render
 * fields the permission gate applies to. Threading a `permissions` prop
 * through every wrapper would be noisy and easy to miss on new fields;
 * context lets any leaf input look up its own gate directly.
 *
 * The provider defaults to a Draft-state permission set so components
 * that render outside the edit form (or in tests without a wrapper)
 * behave the same way they did before this feature — no accidental
 * blanket lockdown.
 */
const defaultPermissions = getPolicyEditPermissions(undefined);

const PolicyEditPermissionsContext = createContext<PolicyEditPermissions>(defaultPermissions);

export const PolicyEditPermissionsProvider: React.FC<{
  policy: Policy | undefined;
  children: React.ReactNode;
}> = ({ policy, children }) => {
  const permissions = useMemo(() => getPolicyEditPermissions(policy), [policy]);
  return (
    <PolicyEditPermissionsContext.Provider value={permissions}>
      {children}
    </PolicyEditPermissionsContext.Provider>
  );
};

export const usePolicyEditPermissions = (): PolicyEditPermissions =>
  useContext(PolicyEditPermissionsContext);

/**
 * Convenience hook for a single field's gate — pairs disabled + reason so
 * the caller can spread them onto an input directly:
 *
 *   const { disabled, reason } = useFieldGate(POLICY_EDIT_FIELD.SUM_INSURED);
 *   <Input disabled={disabled} helperText={reason} ... />
 */
export const useFieldGate = (fieldKey: PolicyEditFieldKey): {
  disabled: boolean;
  reason?: string;
  requiresEndorsement?: boolean;
} => {
  const permissions = usePolicyEditPermissions();
  const gate = permissions.fields[fieldKey];
  return {
    disabled: !gate?.editable,
    reason: gate?.reason,
    requiresEndorsement: gate?.requiresEndorsement,
  };
};
